"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bedReservationSettingsSchema = exports.paymentGatewaySettingsSchema = exports.abhaSettingsSchema = exports.mfaEmergencyOverrideSchema = exports.localizationSettingsSchema = exports.systemSettingsSchema = exports.organizationSettingsSchema = void 0;
const zod_1 = require("zod");
/**
 * Typed keys for the three Stage 2 "settings" surfaces — all three are
 * thin, namespaced wrappers over core/settings (Stage 1.5's Global
 * Configuration Registry), not separate tables. See
 * modules/platform/organization-settings/README.md for why.
 */
exports.organizationSettingsSchema = zod_1.z.object({
    legalName: zod_1.z.string().min(1).max(255).optional(),
    registrationNumber: zod_1.z.string().max(100).optional(),
    website: zod_1.z.string().url().optional(),
});
exports.systemSettingsSchema = zod_1.z.object({
    dateFormat: zod_1.z.string().max(20).optional(),
    timeFormat: zod_1.z.enum(['12h', '24h']).optional(),
    timezone: zod_1.z.string().max(50).optional(),
    currency: zod_1.z.string().length(3).optional(),
    // Phase S1 — MFA role policy. See mfa.service.ts / AuthService for how
    // these two combine: DISABLED is a global kill switch, OPTIONAL means
    // only the listed roles are challenged, REQUIRED means everyone is.
    mfaPolicy: zod_1.z.enum(['DISABLED', 'OPTIONAL', 'REQUIRED']).optional(),
    mfaRequiredRoles: zod_1.z.array(zod_1.z.string().max(100)).optional(),
    mfaRecoveryCodeCount: zod_1.z.union([zod_1.z.literal(5), zod_1.z.literal(10), zod_1.z.literal(20)]).optional(),
    // Stored in hours (not days) so short-lived options like "5 hours" are
    // representable alongside the original day-granularity presets.
    mfaTrustedDeviceHours: zod_1.z
        .union([zod_1.z.literal(5), zod_1.z.literal(8), zod_1.z.literal(24), zod_1.z.literal(168), zod_1.z.literal(720), zod_1.z.literal(1440), zod_1.z.literal(2160)])
        .optional(),
});
exports.localizationSettingsSchema = zod_1.z.object({
    defaultLocale: zod_1.z.string().max(10).optional(),
    supportedLocales: zod_1.z.array(zod_1.z.string().max(10)).optional(),
});
/** Phase S1 — capped at 120 minutes; see SystemSettingsService.activateMfaEmergencyOverride. */
exports.mfaEmergencyOverrideSchema = zod_1.z.object({
    durationMinutes: zod_1.z.number().int().positive().max(120),
});
/**
 * Enterprise Architecture Handbook v1.0, Part IV.1 (Registration
 * Administration, Module 1 — ABHA Settings). Same thin `abha.*`-namespaced
 * wrapper over core/settings as the three above — not a new table.
 * Deliberately excludes a client secret field: a real credential belongs
 * behind `core/secrets`' `SecretProvider`, not the generic settings JSON
 * column (same separation the rest of this codebase already keeps between
 * runtime-editable config and actual secrets).
 */
exports.abhaSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().optional(),
    environment: zod_1.z.enum(['SANDBOX', 'PRODUCTION']).optional(),
    clientId: zod_1.z.string().max(150).optional(),
    callbackUrl: zod_1.z.string().url().optional(),
});
/**
 * Enterprise Architecture Handbook v1.0 (OP Billing Administration,
 * Module 3 — Payment Gateway). Mirrors `abhaSettingsSchema`'s own
 * precedent exactly: non-secret configuration only (provider name,
 * merchant/account identifier, environment, webhook URL). No API
 * key/secret field — that belongs behind `core/secrets`, not this
 * plain `core/settings` wrapper. This is configuration for where a
 * gateway integration WOULD plug in, not a payment-processing
 * implementation (out of this module's Administration-only scope).
 */
exports.paymentGatewaySettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().optional(),
    provider: zod_1.z.string().max(100).optional(),
    environment: zod_1.z.enum(['SANDBOX', 'PRODUCTION']).optional(),
    merchantId: zod_1.z.string().max(150).optional(),
    webhookUrl: zod_1.z.string().url().optional(),
});
/**
 * Enterprise Architecture Handbook v1.0 (IP Billing Administration,
 * Module 4 — Bed Reservation Rules). `Bed.reservedUntil`/`reservedById`/
 * `reservationReason` are already real, operational per-bed fields
 * (Stage 2) — this is the one tenant-wide POLICY those fields don't
 * carry: how long a hold may last before auto-release. A single
 * settings object, not a list, since "bed reservation rule" in
 * practice is one hold-duration policy per tenant, not multiple
 * competing rules.
 */
exports.bedReservationSettingsSchema = zod_1.z.object({
    maxReservationHours: zod_1.z.number().int().positive().max(720).optional(),
    autoReleaseEnabled: zod_1.z.boolean().optional(),
});
//# sourceMappingURL=settings.schema.js.map